import { Check, Heart, Loader2, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";

import { ChanceLegend, ScoreBar, scoreTone } from "@/components/scores";
import { Shell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Link } from "@/lib/router";
import { cn } from "@/lib/utils";
import type { MatchListItem } from "@/shared/types";

type Row = MatchListItem & { token: string };

interface Person {
  name: string;
  slug: string | null;
  score: number;
  overlap: number;
}

export function Matches() {
  const [matches, setMatches] = useState<Row[] | null>(null);
  const [people, setPeople] = useState<Person[] | null>(null);
  const [signedOut, setSignedOut] = useState(false);

  useEffect(() => {
    api
      .get<{ matches: Row[] }>("/api/matches")
      .then((b) => setMatches(b.matches))
      .catch(() => setSignedOut(true));
    api
      .get<{ people: Person[] }>("/api/discover")
      .then((b) => setPeople(b.people))
      .catch(() => {});
  }, []);

  if (signedOut) {
    return (
      <Shell>
        <div className="flex min-h-[65vh] flex-col justify-center py-16">
          <h1 className="text-3xl font-semibold tracking-tight">You're not signed in.</h1>
          <div className="mt-8">
            <Button asChild className="rounded-full px-6">
              <Link href="/join">Send me a link</Link>
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  if (!matches) {
    return (
      <Shell>
        <div className="flex min-h-[60vh] items-center justify-center text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" /> Loading…
        </div>
      </Shell>
    );
  }

  return (
    <Shell
      nav={
        <Button asChild variant="ghost" size="sm" className="rounded-full text-[13px]">
          <Link href="/profile">Your profile</Link>
        </Button>
      }
    >
      <div className="py-12">
        <h1 className="text-3xl font-semibold tracking-[-0.025em] sm:text-4xl">Your matches</h1>
        <p className="mt-3 max-w-2xl text-[15.5px] leading-relaxed text-muted-foreground">
          The matcher writes to you when it finds someone. This page is just a record of what it
          has already sent — there's nothing to check here.
        </p>

        {matches.length === 0 ? (
          <div className="mt-8 rounded-3xl border border-border/70 bg-card/50 p-7 backdrop-blur-sm">
            <Heart className="size-[19px] text-brand" />
            <h2 className="mt-3 text-[19px] font-semibold tracking-tight">
              Nothing yet, and that's normal.
            </h2>
            <p className="mt-2.5 max-w-xl text-[15px] leading-relaxed text-muted-foreground">
              Being ignored by this app is the expected state. Answering more sliders is the only
              thing that makes a match more likely — it gives the matcher more to go on.
            </p>
            <Button asChild variant="outline" className="mt-6 rounded-full px-6">
              <Link href="/profile">Answer a few more</Link>
            </Button>
          </div>
        ) : (
          <>
            <ChanceLegend className="mt-8" />
            <ul className="mt-3 space-y-3">
              {matches.map((m) => (
                <li key={m.matchId}>
                  <Link
                    href={`/match/${m.matchId}/${m.token}`}
                    className="block rounded-2xl border border-border/70 bg-card/50 p-5 backdrop-blur-sm transition-colors hover:border-brand/40"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                      <span className="text-[17px] font-semibold tracking-tight">
                        {m.name}
                        {m.age !== null && (
                          <span className="ml-2 text-[14px] font-normal text-muted-foreground">
                            {m.age}
                          </span>
                        )}
                        {m.countryName && (
                          <span className="ml-2 text-[14px] font-normal text-muted-foreground">
                            {m.countryName}
                          </span>
                        )}
                      </span>
                      <span className={cn("text-[15px] font-semibold", scoreTone(m.score))}>
                        {Math.round(m.score * 100)}%
                      </span>
                    </div>
                    <ScoreBar score={m.score} className="mt-3" />
                    <div className="mt-3 text-[13px] text-muted-foreground">
                      {m.mutual ? (
                        <span className="inline-flex items-center gap-1.5 text-brand">
                          <Check className="size-3.5" /> You both said yes — contact details
                          exchanged
                        </span>
                      ) : m.youAccepted ? (
                        "You said yes. Waiting on them."
                      ) : (
                        "You haven't decided yet."
                      )}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Who you'd score well against, matched or not. */}
        {people && people.length > 0 && (
          <section className="mt-16">
            <h2 className="text-[13px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
              <Sparkles className="mr-1.5 inline size-3.5" />
              Who you line up with
            </h2>
            <p className="mt-3 max-w-2xl text-[14.5px] leading-relaxed text-muted-foreground">
              Everyone who passes your hard filters, ranked. This isn't a feed to browse — it's
              here so you can see the matcher's reasoning. Only people who've turned on a public
              link are openable.
            </p>
            <ChanceLegend className="mt-4" />
            <ul className="mt-3 space-y-2.5">
              {people.map((p, i) => {
                const inner = (
                  <div className="flex items-center gap-4 rounded-xl border border-border/60 bg-card/40 px-4 py-3 backdrop-blur-sm">
                    <span className="w-5 shrink-0 text-[13px] tabular-nums text-muted-foreground/60">
                      {i + 1}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[15px]">{p.name}</span>
                    <ScoreBar score={p.score} className="hidden w-28 shrink-0 sm:block" />
                    <span
                      className={cn(
                        "w-11 shrink-0 text-right text-[14px] font-medium tabular-nums",
                        scoreTone(p.score),
                      )}
                    >
                      {Math.round(p.score * 100)}%
                    </span>
                  </div>
                );
                return (
                  <li key={`${p.name}-${i}`}>
                    {p.slug ? (
                      <Link href={`/u/${p.slug}`} className="block transition-opacity hover:opacity-80">
                        {inner}
                      </Link>
                    ) : (
                      <div className="opacity-70">{inner}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>
    </Shell>
  );
}
